import { DateFormat, utils } from "$lib/utils";
import type {
  AggregateOffer,
  SportsEvent,
  Thing,
  WithContext,
  SportsTeam,
  TouristDestination,
  Question,
  FAQPage,
} from "schema-dts";
import type { IEventTBC } from "types/backend/models/event.entity";
import type { Performer } from "types/backend/models/performer.entity";
import type { IAddress } from "types/backend/models/address.entity";

export type Schema = Thing | WithContext<Thing>;
export interface ITicketSchema {
  title: string;
  datetime: Date | string;
  tbc: IEventTBC;
  slug: string;
  subcategory: {
    title: string;
    slug: string;
  };
  performers?: Partial<Performer>[];
  venue?: {
    title: string;
    address?: Partial<IAddress>;
  };
  qty?: number;
  priceFrom?: {
    proceedPrice: number;
    currency: string;
  };
}

function venueSchema(venue?: { title: string; address?: Partial<IAddress> }) {
  if (!venue) {
    return {};
  }

  const { title: venueTitle, address } = venue;
  return {
    location: {
      "@type": "Place",
      name: venueTitle,
      address: address
        ? {
            "@type": "PostalAddress",
            streetAddress: [address?.line1, address?.line2].filter(v => v).join(", "),
            addressLocality: address?.city,
            postalCode: address?.postcode,
            addressCountry: address?.country ? { "@type": "Country", name: address.country } : undefined,
          }
        : undefined,
    },
  } as unknown as TouristDestination;
}

function performerSchema(performers: Partial<Performer>[]) {
  if (!performers?.length) {
    return {};
  }

  const [p1, p2] = performers;
  const homeTeam: SportsTeam = {
    "@type": "SportsTeam",
    name: p1?.title || "n/a",
  };
  const awayTeam: SportsTeam = {
    "@type": "SportsTeam",
    name: p2?.title || "n/a",
  };
  return {
    homeTeam: p1 ? homeTeam : undefined,
    awayTeam: p2 ? awayTeam : undefined,
    performer: [homeTeam, awayTeam].filter(v => v),
  };
}

function offerSchema(slug: string, offer?: { currency: string; proceedPrice: number }, qty?: number) {
  if (!offer) {
    return {};
  }

  return {
    offers: {
      "@type": "AggregateOffer",
      name: "Resale",
      category: "Secondary",
      availability: "https://schema.org/InStock",
      priceCurrency: offer.currency,
      lowPrice: offer.proceedPrice,
      highPrice: "N/A",
      validFrom: "today",
      inventoryLevel: qty ? { "@type": "QuantitativeValue", value: qty } : undefined,
      url: slug,
    } as AggregateOffer,
  };
}

export function serializeSchema(thing: Schema) {
  return `<script type="application/ld+json">${JSON.stringify(thing)}</script>`;
}

export class FAQSchema {
  private qa: Question[] = [];

  public addQA(question: string, answer: string) {
    this.qa.push({
      "@type": "Question",
      name: question,
      acceptedAnswer: {
        "@type": "Answer",
        text: this.stripHtmlTags(answer),
      },
    });
  }

  public toLDJson() {
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: this.qa,
    } as FAQPage;
  }

  private stripHtmlTags(html: string): string {
    // Replace all HTML tags with an empty string
    return html.replace(/<\/?[^>]+(>|$)|\n/g, "");
  }
}

const domain = "https://www.footballticketpad.com";
export function ticketToSchema(ticket: ITicketSchema) {
  const { title, datetime, slug, subcategory, venue, priceFrom, performers = [], tbc, qty } = ticket;
  let dtStr = "";
  let endDtStr = "";
  if (tbc.date) {
    const dt = new Date(datetime);
    endDtStr = dtStr = isNaN(dt.getTime()) ? "TBC" : utils.getDateAndTime(datetime, "en-GB", DateFormat.iso);
  } else {
    dtStr = utils.getDateAndTime(datetime, "en-GB", DateFormat.isodt);
    endDtStr = utils.getDateAndTime(utils.addDT(datetime, "min", 90), "en-GB", DateFormat.isodt);
  }

  const logo = `${domain}/assets/css/images/logo.png`;

  const sportsEventSchema: WithContext<SportsEvent> = {
    "@context": "https://schema.org",
    "@type": "SportsEvent",
    image: logo,
    sport: "football",
    name: title,
    startDate: dtStr,
    endDate: endDtStr,
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    eventStatus: "https://schema.org/EventScheduled",
    // subEvent: {
    //   "@type": "Event",
    //   name: subcategory?.title || "",
    //   startDate: dtStr,
    //   endDate: dtStr,
    //   ...venueSchema(venue),
    // },
    organizer: {
      "@type": "Organization",
      name: subcategory?.title || "",
      url: `${domain}${subcategory?.slug}` || "",
    },
    url: `${domain}${slug}`,
    description: `${title} Tickets`,
    ...venueSchema(venue),
    ...performerSchema(performers),
    ...offerSchema(`${domain}${slug}`, priceFrom, qty),
  };

  return sportsEventSchema;
}

export function titleDateSchema(event: {
  title: string;
  datetime: string | Date;
  slug: string;
  tbc: IEventTBC;
  subcategory: { slug: string; title: string };
}) {
  const { title, datetime, slug, tbc, subcategory } = event;
  const dtStr = tbc?.date ? "TBC" : utils.getDateAndTime(datetime, "en-GB", DateFormat.isodt);
  const sportsEventSchema: WithContext<SportsEvent> = {
    "@context": "https://schema.org",
    "@type": "SportsEvent",
    sport: "football", // This can be modified or omitted if not needed
    name: title,
    startDate: dtStr,
    endDate: dtStr,
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    eventStatus: "https://schema.org/EventScheduled",
    description: `${title} Tickets`,
    url: `${subcategory?.slug}${slug}`, // Optional: URL can be set if available
    // subEvent: {
    //   "@type": "Event",
    //   name: subcategory?.title || "",
    // },
    organizer: {
      "@type": "Organization",
      name: subcategory?.title || "",
      url: subcategory?.slug || "",
    },
  };

  return sportsEventSchema;
}

export function toBreadCrumb(list: { url?: string; name: string }[]) {
  return serializeSchema({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: list.map((path, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: path.name,
      item: domain + path.url,
    })),
  });
}
